import { FormEvent, useState } from "react"
import { router } from "@inertiajs/react"
import {
  Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography
} from "@mui/material"
import FileField from "../../../components/FileField"
import AudioPlayer from "../../../components/AudioPlayer"
import TranslationsEditor, { TranslationDraft, appendTranslationsFormData } from "../../../components/TranslationsEditor"

export type SentenceAudio = {
  url: string
  language: string
  ai_model: string
  voice: string
}

export type SentenceForm = {
  id: number | null
  kanji: string
  kana: string
  romaji: string
  translations: TranslationDraft[]
  audio: SentenceAudio | null
  topic_ids: number[]
}

type Props = {
  sentence: SentenceForm
  topics: { id: number; name: string }[]
  errors?: Record<string, string[]>
}

export default function SentenceFormView({ sentence, topics, errors }: Props) {
  const isNew = !sentence.id
  const [kanji, setKanji] = useState(sentence.kanji ?? "")
  const [kana, setKana] = useState(sentence.kana ?? "")
  const [romaji, setRomaji] = useState(sentence.romaji ?? "")
  const [translations, setTranslations] = useState<TranslationDraft[]>(sentence.translations ?? [])
  const [audio, setAudio] = useState<File | null>(null)
  const [topicIds, setTopicIds] = useState<number[]>(sentence.topic_ids ?? [])

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    const data = new FormData()
    data.append("sentence[kanji]", kanji)
    data.append("sentence[kana]", kana)
    data.append("sentence[romaji]", romaji)
    appendTranslationsFormData(data, "sentence", translations)
    if (audio) data.append("sentence[audio]", audio)
    topicIds.forEach((id) => data.append("sentence[topic_ids][]", String(id)))
    if (topicIds.length === 0) data.append("sentence[topic_ids][]", "")

    const url = isNew ? "/admin/sentences" : `/admin/sentences/${sentence.id}`
    if (!isNew) data.append("_method", "patch")
    router.post(url, data, { forceFormData: true })
  }

  return (
    <Box component="form" onSubmit={onSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="h5">{isNew ? "New sentence" : "Edit sentence"}</Typography>
      <TextField label="Kanji" value={kanji} onChange={(e) => setKanji(e.target.value)}
                 slotProps={{ htmlInput: { "data-testid": "sentence-kanji" } }} />
      <TextField label="Kana" value={kana} onChange={(e) => setKana(e.target.value)}
                 slotProps={{ htmlInput: { "data-testid": "sentence-kana" } }}
                 error={!!errors?.kana} helperText={errors?.kana?.join(", ")} />
      <TextField label="Romaji" value={romaji} onChange={(e) => setRomaji(e.target.value)}
                 slotProps={{ htmlInput: { "data-testid": "sentence-romaji" } }} />
      <Box>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Translations</Typography>
        <TranslationsEditor
          translations={translations} onChange={setTranslations} withLiteral
          testIdPrefix="sentence-translation"
        />
      </Box>
      <FormControl fullWidth>
        <InputLabel id="sentence-topics-label">Topics</InputLabel>
        <Select
          labelId="sentence-topics-label" multiple value={topicIds} label="Topics"
          onChange={(e) => setTopicIds(e.target.value as number[])}
          renderValue={(selected) => topics.filter((t) => selected.includes(t.id)).map((t) => t.name).join(", ")}
        >
          {topics.map((t) => (
            <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Current audio</Typography>
        <AudioPlayer src={sentence.audio?.url ?? null} />
        {sentence.audio && (
          <Typography variant="caption" color="text.secondary">
            {sentence.audio.language} · {sentence.audio.ai_model} · {sentence.audio.voice}
          </Typography>
        )}
      </Box>
      <FileField label="Replace audio" accept="audio/*" name="audio"
                 onSelect={setAudio} selectedName={audio?.name ?? null}
                 existingUrl={sentence.audio?.url ?? null} testId="sentence-audio" />
      <Stack direction="row" gap={2}>
        <Button type="submit" variant="contained" data-testid="sentence-submit">
          {isNew ? "Create" : "Save changes"}
        </Button>
        <Button onClick={() => history.back()}>Cancel</Button>
      </Stack>
      {errors?.base && (
        <Typography color="error" data-testid="sentence-errors">{errors.base.join(", ")}</Typography>
      )}
    </Box>
  )
}
